import { ActiveCurrency } from '@contexts/store'
import { allEqual } from '@helper/globalHelpers'
import { useMemo } from 'react'

export function formatPrice({
  amount,
  currencyCode,
  locale,
}: {
  amount: number
  currencyCode: string
  locale?: string
}) {
  const thisLocale = locale ?? 'en'
  const formatCurrency = new Intl.NumberFormat(thisLocale, {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: currencyCode ?? 'NGN',
  })

  return formatCurrency.format(amount)
}

export function formatVariantPrice({
  amount,
  baseAmount,
  currencyCode,
  locale,
}: {
  baseAmount: number
  amount: number
  currencyCode: string
  locale: string
}) {
  const hasDiscount = baseAmount > amount
  const formatDiscount = new Intl.NumberFormat(locale, { style: 'percent' })
  const discount = hasDiscount
    ? formatDiscount.format((baseAmount - amount) / baseAmount)
    : null

  const price = formatPrice({ amount, currencyCode, locale })
  const basePrice = hasDiscount
    ? formatPrice({ amount: baseAmount, currencyCode, locale })
    : null

  return { price, basePrice, discount }
}

export function convertPrice(
  data?: {
    amount: number
    baseAmount?: number
    currency: {
      _id?: string
      code: string
      symbol: string
      rate: number
    } | null
  } | null,
) {
  const { amount, baseAmount, currency } = data ?? {}
  const locale = 'en'
  if (typeof amount !== 'number' || !currency) return ''
  let value: any = ''
  // console.log('currency', currency)
  const { code: currencyCode, rate: currencyRate } = currency
  let amountObj = {
    amount: currencyRate ? amount * currencyRate : amount,
    currencyCode,
    locale,
  }

  if (baseAmount) {
    // @ts-expect-error
    amountObj.baseAmount = currencyRate ? baseAmount * currencyRate : baseAmount
    // @ts-expect-error
    value = formatVariantPrice(amountObj)
  } else {
    value = formatPrice(amountObj)
  }

  return typeof value === 'string'
    ? { price: value, basePrice: null, discount: null }
    : value
}

export default function usePrice(
  data?: {
    amount: number
    baseAmount?: number
    currency: ActiveCurrency | null
  } | null,
) {
  const { amount, baseAmount, currency } = data ?? {}
  const value = useMemo(() => {
    if (typeof amount !== 'number' || !currency) return ''
    return convertPrice({ amount, baseAmount, currency })
  }, [amount, baseAmount, currency])

  return typeof value === 'string'
    ? { price: value, basePrice: null, discount: null }
    : value
}

type response = {
  price: number
  min: number
  max: number
  display: string
  allSame: boolean
  hasSale: boolean
}
export const getPriceRange = (product: any) => {
  let response: response = {
    price: 0,
    min: 0,
    max: 0,
    display: '-',
    allSame: false,
    hasSale: false,
  }
  if (!product) return response
  let min = 0
  let max = 0
  let allSame = false
  let hasSale = false
  if (product?.has_variants || product?.variants?.length > 0) {
    const normalPrices = product.variants.map((v: any) =>
      v?.price ? v?.price : 0,
    )
    const salePrices = product.variants.map((v: any) =>
      v?.sale_price ? v?.sale_price : 0,
    )
    hasSale = product.variants.some((v: any) =>
      v?.sale_price > 0 && v?.sale_price !== v?.price && v?.stock > 0
        ? true
        : false,
    )
    let priceArray = [...normalPrices, ...salePrices]
    max = Math.max(...priceArray)
    if (max > 0) {
      priceArray = priceArray.filter((price: number) => price > 0)
    }
    min = Math.min(...priceArray)
    allSame = allEqual(normalPrices) && allEqual(salePrices)

    // if ((isEmpty(priceArray) || min === max) && allSame) {
    //   min = product?.sale_price > 0 ? product?.sale_price : product?.price
    //   max = product?.price
    // }
  } else {
    const currentDate = new Date()
    const salePriceStart = product?.salePriceStart
      ? new Date(product.salePriceStart)
      : null
    const salePriceEnd = product?.salePriceEnd
      ? new Date(product.salePriceEnd)
      : null

    const isSaleActive =
      product?.sale_price > 0 &&
      product?.sale_price !== product?.price &&
      (!salePriceStart || currentDate >= salePriceStart) &&
      (!salePriceEnd || currentDate <= salePriceEnd)

    hasSale = isSaleActive
    min = isSaleActive ? product?.sale_price : product?.price
    max = product?.price
    allSame = min === max && min > 0 ? true : false
  }

  response.price = min === max ? min : 0
  response.min = min ?? 0
  response.max = max ?? 0
  response.allSame = allSame
  response.hasSale = hasSale
  response.display =
    response.min === response.max
      ? response.min.toString()
      : `${response.min} - ${response.max}`

  // console.log("name", product.name, 'response', response)
  return response
}
